"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import {
    StatsTable,
    EntityDisplay,
    type StatsTableColumn,
    type StatsTableRow,
} from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { LeaderEntry } from "@/utilities/data/leaders-types";

const tableStyles = {
    container: "table-surface",
    borderRadius: "none" as const,
    shadow: "none" as const,
    typography: {
        cell: "text-sm font-medium",
        header: "text-xs uppercase font-bold opacity-70",
    },
};

export interface LeaderboardCategoryProps {
    title: string;
    entries: LeaderEntry[];
    statHeader: string;
    statLabel: string;
    playerLabel: string;
    allowPlayerPhotos: boolean;
    /** Show #1 as a photo card above the table. Default true. */
    showFeaturedPlayer?: boolean;
    /** Number of rows to display (including the featured player). Default = entries.length. */
    rowCount?: number;
}

interface FeaturedProps {
    entry: LeaderEntry;
    statLabel: string;
    allowPlayerPhotos: boolean;
}

const FeaturedPlayer: React.FC<FeaturedProps> = ({ entry, statLabel, allowPlayerPhotos }) => {
    const nav = useNavigation();
    const photo = allowPlayerPhotos ? entry.player.photo : undefined;
    const teamLogo = entry.team?.logo;
    const teamName = entry.team?.name;
    const teamHref = entry.team?.teamId ? nav.team(entry.team.teamId) : undefined;
    const playerHref = entry.player.playerId ? nav.player(entry.player.playerId) : undefined;

    const initial = (
        <div className="w-full h-full flex items-center justify-center text-card-foreground/50 text-2xl font-bold">
            {entry.player.name.charAt(0)}
        </div>
    );
    const avatar = photo ? (
        <img src={photo} alt={entry.player.name} className="w-full h-full object-cover" />
    ) : (
        initial
    );

    return (
        <div className="p-4 flex items-center gap-4 bg-muted text-card-foreground">
            <div className="relative">
                {playerHref ? (
                    <Link
                        href={playerHref}
                        prefetch={false}
                        className="block w-16 h-16 rounded-full overflow-hidden bg-card border border-primary shrink-0"
                    >
                        {avatar}
                    </Link>
                ) : (
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-card border border-primary shrink-0">
                        {avatar}
                    </div>
                )}
                <span className="absolute -bottom-1 -left-1 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">
                    1
                </span>
            </div>
            <div className="flex-1 min-w-0">
                {playerHref ? (
                    <Link
                        href={playerHref}
                        prefetch={false}
                        className="block font-semibold text-card-foreground hover:underline truncate"
                    >
                        {entry.player.name}
                    </Link>
                ) : (
                    <p className="font-semibold text-card-foreground truncate">{entry.player.name}</p>
                )}
                {teamName && (
                    <div className="flex items-center gap-1.5 mt-1">
                        <TeamLogo
                            src={teamLogo}
                            size="xs"
                            primaryColor={entry.team?.primaryColor}
                            secondaryColor={entry.team?.secondaryColor}
                            countryCode={entry.team?.countryCode}
                            countryFlag={entry.team?.countryFlag}
                        />
                        {teamHref ? (
                            <Link
                                href={teamHref}
                                prefetch={false}
                                className="text-xs text-card-foreground/70 hover:text-card-foreground hover:underline truncate"
                            >
                                {teamName}
                            </Link>
                        ) : (
                            <span className="text-xs text-card-foreground/70 truncate">{teamName}</span>
                        )}
                    </div>
                )}
            </div>
            <div className="text-right shrink-0">
                <p className="text-3xl font-bold text-primary">{entry.value}</p>
                <p className="text-xs text-card-foreground/70 uppercase">{statLabel}</p>
            </div>
        </div>
    );
};

export const LeaderboardCategory: React.FC<LeaderboardCategoryProps> = ({
    title,
    entries,
    statHeader,
    statLabel,
    playerLabel,
    allowPlayerPhotos,
    showFeaturedPlayer = true,
    rowCount,
}) => {
    const nav = useNavigation();

    const visible = typeof rowCount === "number" ? entries.slice(0, rowCount) : entries;
    const [featured, ...rest] = visible;
    const tableEntries = showFeaturedPlayer ? rest : visible;
    const showSeparator = showFeaturedPlayer && featured && tableEntries.length > 0;

    const columns: StatsTableColumn[] = useMemo(
        () => [
            { key: "rank", header: "#", align: "left", width: "xs" },
            {
                key: "player",
                header: playerLabel,
                align: "left",
                width: "flex",
                render: (v) => {
                    const p = v as {
                        name: string;
                        photo?: string;
                        playerId?: string;
                        teamLogo?: string;
                        primaryColor?: string;
                        secondaryColor?: string;
                        countryCode?: string;
                        countryFlag?: string;
                    };
                    const playerImage = allowPlayerPhotos ? p.photo : undefined;
                    const content = (
                        <EntityDisplay
                            name={p.name}
                            imageUrl={playerImage || undefined}
                            image={
                                !playerImage ? (
                                    <TeamLogo
                                        src={p.teamLogo}
                                        size="sm"
                                        primaryColor={p.primaryColor}
                                        secondaryColor={p.secondaryColor}
                                        countryCode={p.countryCode}
                                        countryFlag={p.countryFlag}
                                    />
                                ) : undefined
                            }
                        />
                    );
                    if (p.playerId) {
                        return (
                            <Link
                                href={nav.player(p.playerId)}
                                prefetch={false}
                                className="hover:underline"
                            >
                                {content}
                            </Link>
                        );
                    }
                    return content;
                },
            },
            {
                key: "value",
                header: statHeader,
                align: "right",
                width: "lg",
                className: "font-bold text-primary",
            },
        ],
        [statHeader, playerLabel, allowPlayerPhotos, nav]
    );

    const rows: StatsTableRow[] = useMemo(
        () =>
            tableEntries.map((entry) => ({
                id: entry.id,
                rank: entry.rank,
                player: {
                    name: entry.player.name,
                    photo: entry.player.photo,
                    playerId: entry.player.playerId,
                    teamLogo: entry.team?.logo,
                    primaryColor: entry.team?.primaryColor,
                    secondaryColor: entry.team?.secondaryColor,
                    countryCode: entry.team?.countryCode,
                    countryFlag: entry.team?.countryFlag,
                },
                value: entry.value,
            })),
        [tableEntries]
    );

    if (visible.length === 0) return null;

    return (
        <div>
            <h3 className="text-xs uppercase font-bold text-muted-foreground mb-2">{title}</h3>
            <div className="rounded-lg overflow-hidden border border-border">
                {showFeaturedPlayer && featured && (
                    <FeaturedPlayer entry={featured} statLabel={statLabel} allowPlayerPhotos={allowPlayerPhotos} />
                )}
                {showSeparator && <div className="border-t border-border" />}
                {rows.length > 0 && <StatsTable columns={columns} rows={rows} styles={tableStyles} showHeader={false} />}
            </div>
        </div>
    );
};
